import { useState } from 'react';

import { Paper, SegmentedControl, Stack, Text, Textarea } from '@mantine/core';

import { MarkdownParser } from '@/components/Containers/MarkdownParser';

interface ProseEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
  /** The house's text for the same section, shown for reference. */
  inherited?: string;
  minRows?: number;
}

/** A prose section as a document: Markdown, with a preview — and the house's
 * prose beside it when the client's adds to it. */
export function ProseEditor({
  label,
  value,
  onChange,
  description,
  inherited,
  minRows = 8,
}: Readonly<ProseEditorProps>) {
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  return (
    <Stack gap="xs">
      {inherited ? (
        <Paper withBorder p="sm" radius="sm" bg="var(--mantine-color-gray-0)">
          <Text size="xs" fw={600} c="dimmed" mb={4}>
            From the house playbook (read-only)
          </Text>
          <MarkdownParser content={inherited} />
        </Paper>
      ) : null}
      <SegmentedControl
        size="xs"
        w="fit-content"
        value={mode}
        onChange={(next) => setMode(next as 'write' | 'preview')}
        data={[
          { value: 'write', label: 'Write' },
          { value: 'preview', label: 'Preview' },
        ]}
      />
      {mode === 'write' ? (
        <Textarea
          label={label}
          description={description}
          value={value}
          autosize
          minRows={minRows}
          styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      ) : (
        <Paper withBorder p="sm" radius="sm" mih={120}>
          {value.trim() ? (
            <MarkdownParser content={value} />
          ) : (
            <Text size="sm" c="dimmed">
              Nothing written yet.
            </Text>
          )}
        </Paper>
      )}
    </Stack>
  );
}
